import { useQuery } from 'hooks/useCustomQuery';
import { useMutation } from '@tanstack/react-query';
import {
    getAllAPIUsers,
    createAPIUser,
    getAPIUser,
    editAPIUser,
    deleteAPIUser,
    resetAPIUser,
} from 'API/APIKeys';
import { toast } from 'react-toastify';
import moment from 'moment';

export const useGetAllAPIUsers = (currentCompany, setTableData, dispatch) => {
    return useQuery({
        queryKey: ['apiUsers'],
        queryFn: async () => {
            const data = await getAllAPIUsers(currentCompany);
            return data;
        },
        onSuccess: async (data) => {
            setTableData((prev) => ({
                ...prev,
                data: data,
            }));
            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
        },
        refetchOnWindowFocus: false,
    });
};

export const useCreateAPIUser = (setApiKey, dispatch, onOpen) => {
    return useMutation({
        mutationFn: async (apiUserData) => {
            try {
                const res = await createAPIUser(apiUserData);
                toast.success('API User created successfully!');
                setApiKey(res.apiKey);
                onOpen();
                return res;
            } catch (error) {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.title;
                toast.error(errorMessage);
                throw new Error(errorMessage || 'Failed to create API User');
            }
        },
    });
};

export const useGetAPIUser = (
    apiUserId,
    setApiUser,
    setLoadingFalse,
    setLaoadingTrue,
    setApiKey,
) => {
    return useQuery({
        queryKey: ['apiUser', { apiUserId }],
        queryFn: async () => {
            try {
                setLaoadingTrue;
                const data = await getAPIUser(apiUserId).then((res) => {
                    const localDateTime = moment.utc(res.expiration).local();
                    setApiUser({
                        ...res,
                        expirationDate: new Date(localDateTime),
                        expirationTime: moment(localDateTime).format('HH:mm'),
                    });
                    setApiKey(res.apiKey);
                    setLoadingFalse;
                    return res;
                });

                return data;
            } catch (error) {
                toast.error(error?.message || 'Failed to fetch API User');
                throw new Error(error?.message || 'Failed to fetch API User');
            }
        },
        enabled: Boolean(apiUserId),
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
        cacheTime: 0,
    });
};

export const useEditAPIUser = (apiUser, dispatch, navigate) => {
    return useMutation({
        mutationFn: async () => {
            try {
                editAPIUser(apiUser).then(() => {
                    toast.success('API User updated successfully!');
                    dispatch({ type: 'RESET_TABLE' });
                    navigate('/admins', { state: { index: 1 } });
                });
            } catch (error) {
                toast.error(error?.message || 'Failed to update API User');
                throw new Error(error?.message || 'Failed to update API User');
            }
        },
    });
};

export const useDeleteAPIUser = (
    apiUserId,
    dispatch,
    navigate,
    setIsDeleteBusy,
) => {
    return useMutation({
        mutationFn: async () => {
            try {
                setIsDeleteBusy(true);
                deleteAPIUser(apiUserId).then(() => {
                    toast.success('API User deleted successfully!');
                    dispatch({
                        type: 'RESET_TABLE',
                    });
                    navigate('/admins', { state: { index: 1 } });
                });
            } catch (error) {
                toast.error(error?.response?.data?.message);
                throw new Error(error?.message || 'Failed to delete API User');
            } finally {
                setIsDeleteBusy(false);
            }
        },
    });
};

export const useResetAPIUser = (
    apiUserId,
    dispatch,
    navigate,
    setIsResetBusy,
    onResetClose,
    setApiKey,
    onApiOpen,
) => {
    return useMutation({
        mutationFn: async () => {
            try {
                setIsResetBusy(true);
                resetAPIUser(apiUserId).then((res) => {
                    dispatch({ type: 'RESET_TABLE' });
                    setApiKey(res.apiKey);
                    onResetClose();
                    onApiOpen();
                    setApiKey(res.apiKey);
                    toast.success('API User reset successfully!');
                });
            } catch (error) {
                toast.error(error?.response?.data?.message);
            } finally {
                setIsResetBusy(false);
            }
        },
    });
};
